import { useEffect, useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAssistant } from '@/contexts/AssistantContext';
import { cn } from '@/utils/cn';
import { AssistantPanel } from './AssistantPanel';
import { AssistantRail } from './AssistantRail';
import { AssistantSheet } from './AssistantSheet';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

const DOCKED_WIDTH = 420;
const RAIL_WIDTH = 56;

/**
 * Places the assistant: docked/rail/closed aside on desktop, scrim + dialog on
 * tablet. The chat stays mounted while collapsed so replies keep streaming.
 * Must be rendered inside a `relative` content row below the top bar.
 */
export function AssistantHost() {
  const { t } = useTranslation();
  const {
    access,
    presentation,
    desktopMode,
    overlayOpen,
    refsVisible,
    activeSessionId,
    collapse,
    closeOverlay,
    firstRunActive,
  } = useAssistant();
  const dialogRef = useRef<HTMLDivElement>(null);
  const overlayActive = presentation === 'overlay' && overlayOpen;

  useEffect(() => {
    if (!overlayActive) return;
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeOverlay();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const nodes = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === dialogRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, [overlayActive, closeOverlay]);

  if (!access.hasAccess) return null;

  if (presentation === 'docked') {
    const docked = desktopMode === 'docked';
    const width =
      desktopMode === 'docked'
        ? DOCKED_WIDTH
        : desktopMode === 'rail'
          ? RAIL_WIDTH
          : 0;
    const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Escape' && docked) collapse();
    };
    return (
      <aside
        role="complementary"
        aria-label={t('assistant.panel.regionLabel')}
        style={{ width }}
        onKeyDown={onKeyDown}
        className={cn(
          'h-full shrink-0 overflow-hidden bg-[var(--background-page-content)] transition-[width] duration-200 ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none',
          width > 0 && 'border-l border-[var(--border-default)]',
        )}
      >
        {desktopMode === 'rail' && <AssistantRail />}
        {(docked || activeSessionId) && (
          <div
            hidden={!docked}
            className="h-full"
            style={{ width: DOCKED_WIDTH }}
          >
            <AssistantPanel
              mode="desktop"
              closeAction="collapse"
              onCloseAction={collapse}
            />
          </div>
        )}
      </aside>
    );
  }

  if (presentation === 'overlay') {
    const keepMounted = overlayOpen || !!activeSessionId;
    return (
      <>
        {overlayOpen && (
          <div
            data-testid="assistant-scrim"
            aria-hidden="true"
            onClick={closeOverlay}
            className="absolute inset-0 z-30 bg-[var(--scrim)] motion-safe:animate-in motion-safe:fade-in"
          />
        )}
        {keepMounted && (
          <div
            ref={dialogRef}
            role={overlayOpen ? 'dialog' : undefined}
            aria-modal={overlayOpen ? true : undefined}
            aria-label={t('assistant.panel.regionLabel')}
            tabIndex={-1}
            hidden={!overlayOpen}
            className="absolute bottom-0 right-0 top-0 z-40 w-[400px] max-w-full bg-[var(--background-page-content)] shadow-[var(--shadow-sheet)] outline-none motion-safe:animate-in motion-safe:slide-in-from-right motion-safe:duration-200"
          >
            <AssistantPanel
              mode="desktop"
              closeAction="close"
              onCloseAction={closeOverlay}
            />
          </div>
        )}
      </>
    );
  }

  // Sheet presentation: the sheet (and panel) stays at a stable tree position
  // and is hidden when closed so replies keep streaming.
  const sheetPanel = (
    <AssistantPanel
      mode="mobile"
      closeAction="close"
      onCloseAction={closeOverlay}
    />
  );
  return overlayOpen || activeSessionId ? (
    <AssistantSheet
      open={overlayOpen}
      onClose={closeOverlay}
      showScrim={!refsVisible}
      initialSnap={firstRunActive ? 'full' : 'half'}
    >
      {sheetPanel}
    </AssistantSheet>
  ) : null;
}
